// Fila de saques do admin (`GET /admin/v1/withdrawals`) com as abas e as colunas do
// croqui (`screens/admin/saques.tsx`): Seller · Valor · Taxa · Chave PIX · Solicitado há
// · Adquirente · ação. Foco declarado (`spec/17` §8.2): **a fila `awaiting`**, a tela
// abre nela, a linha mais antiga primeiro, com "Aprovar" na própria linha para não
// obrigar a abrir o detalhe.
//
// Componente puro: quem chama a API é a rota. Skeleton, vazio e erro (com `request_id`)
// vêm do `Table` do kit.

import type { AdminWithdrawal } from '../../api/queries/admin-withdrawals.ts';
import { Amount } from '../../components/kit/amount.tsx';
import { Btn } from '../../components/kit/btn.tsx';
import { ListToolbar } from '../../components/kit/list-toolbar.tsx';
import { Mono } from '../../components/kit/mono.tsx';
import { Notice } from '../../components/kit/notice.tsx';
import { Pager } from '../../components/kit/pager.tsx';
import { Pill } from '../../components/kit/pill.tsx';
import type { SegItem } from '../../components/kit/segmented.tsx';
import { type Col, Table } from '../../components/kit/table.tsx';
import { formatDateTime } from '../../lib/dates.ts';
// Import entre features é leitura, não edição: `STATUS_META` é o mesmo contrato gerado
// (`WithdrawalStatus`) e duplicá-lo deixaria os dois painéis com rótulos diferentes para
// o mesmo status. Ver "Questões abertas" de WEB-23.
import { SellerFilter } from '../admin-sellers/seller-filter.tsx';
import { SellerIdentity, sellerTitle } from '../admin-sellers/seller-identity.tsx';
import { STATUS_META } from '../withdrawals/status.ts';
import type { AdminWithdrawalTotals } from './queries.ts';
import { ageOf, canDecide, TAB_IDS, TAB_LABEL, type TabId, tabTotals } from './rules.ts';

/** A tela abre na fila (`spec/17` §8.2), não no histórico inteiro. */
export const QUEUE_TAB: TabId = 'awaiting';

/**
 * Banner do kill switch. Aprovar continua funcionando com ele ligado, quem segura o
 * dinheiro é o worker (`execute-payouts` pula com `payouts_paused`), então o texto diz
 * exatamente isso em vez de prometer que a aprovação vai falhar. Copy do croqui.
 */
export function PayoutsPausedBanner({
  reason,
  onOpenSettings,
}: {
  reason: string | null;
  onOpenSettings: () => void;
}) {
  return (
    <Notice
      tone="bad"
      icon="lock"
      title="Saques pausados globalmente."
      action={
        <Btn kind="card" small onClick={onOpenSettings}>
          Abrir configurações
        </Btn>
      }
    >
      Aprovar continua registrando a decisão, mas nenhum payout sai enquanto o kill switch estiver
      ligado.
      {reason === null ? '' : ` Motivo: ${reason}`}
    </Notice>
  );
}

function columnsFor(
  onSelect: (withdrawal: AdminWithdrawal) => void,
  onApprove: (withdrawal: AdminWithdrawal) => void,
  onFilterSeller: (sellerId: string) => void,
  approvingId: string | undefined,
  now: Date | undefined,
): Col<AdminWithdrawal>[] {
  return [
    {
      // Só no celular: a coluna do nome (abaixo) é um botão e não pode ser `compact`.
      key: 'seller-name',
      label: 'Seller',
      compact: true,
      mobileOnly: true,
      render: (row) => <span className="font-medium">{sellerTitle(row.seller)}</span>,
    },
    {
      key: 'seller',
      // NÃO marcar `compact`: a célula tem botões e as colunas `compact` viram o
      // conteúdo do `<button>` do cartão de 390px (`<button>` dentro de `<button>`).
      label: 'Seller',
      render: (row) => (
        <SellerIdentity
          variant="cell"
          seller={row.seller}
          onOpen={() => {
            onSelect(row);
          }}
          onFilter={() => {
            onFilterSeller(row.seller.id);
          }}
        />
      ),
    },
    {
      key: 'amount',
      label: 'Valor',
      w: '124px',
      align: 'right',
      compact: true,
      render: (row) => <Amount cents={row.amount} ink="text" />,
    },
    {
      key: 'fee',
      label: 'Taxa',
      w: '88px',
      align: 'right',
      render: (row) => <Amount cents={row.fee} ink="muted" weight={500} />,
    },
    {
      key: 'key',
      label: 'Chave PIX',
      w: '210px',
      compact: true,
      // `pix_key_masked` do objeto admin (API-27): a listagem já entrega a chave
      // mascarada, sem precisar abrir o detalhe.
      render: (row) => (
        <Mono size={12} ink="muted">
          {row.pix_key_masked}
        </Mono>
      ),
    },
    {
      key: 'age',
      label: 'Solicitado há',
      w: '128px',
      compact: true,
      render: (row) => (
        <span title={formatDateTime(row.created_at)}>
          <Mono size={12} ink="muted">
            {ageOf(row.created_at, now)}
          </Mono>
        </span>
      ),
    },
    {
      key: 'acquirer',
      label: 'Adquirente',
      w: '110px',
      render: (row) => (
        <Mono size={12} ink="text">
          {row.acquirer.slug}
        </Mono>
      ),
    },
    {
      key: 'action',
      label: '',
      w: '140px',
      align: 'right',
      // Fora de `awaiting` a coluna é o status: é assim no croqui, e é o que faz a aba
      // "Pagos" ainda dizer o que cada linha é.
      render: (row) =>
        canDecide(row.status) ? (
          // O `Btn` do kit não repassa o evento (`onClick: () => void`), e sem parar a
          // propagação o clique sobe para a `<tr>` e o detalhe abriria junto. O `onClick`
          // do invólucro roda DEPOIS do botão (fase de bolha), então só barra a subida.
          // biome-ignore lint/a11y/noStaticElementInteractions: não é um controle, só barra a bolha do botão de dentro.
          <span
            className="inline-flex"
            onClick={(event) => {
              event.stopPropagation();
            }}
            onKeyUp={(event) => {
              event.stopPropagation();
            }}
          >
            <Btn
              kind="card"
              small
              icon="lock"
              disabled={approvingId !== undefined}
              aria-label={`Aprovar saque de ${row.seller.display_name}`}
              onClick={() => {
                onApprove(row);
              }}
            >
              {approvingId === row.id ? 'Aprovando…' : 'Aprovar'}
            </Btn>
          </span>
        ) : (
          <Pill tone={STATUS_META[row.status].tone}>{STATUS_META[row.status].label}</Pill>
        ),
    },
  ];
}

export interface AdminWithdrawalsQueueProps {
  rows: AdminWithdrawal[];
  tab: TabId;
  onTabChange: (tab: TabId) => void;
  /** Agregado de `with_totals=1` (API-27); `undefined` enquanto a página não chegou. */
  totals: AdminWithdrawalTotals | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onSelect: (withdrawal: AdminWithdrawal) => void;
  /** Aprovar direto na linha; o step-up é do `api/client.ts`. */
  onApprove: (withdrawal: AdminWithdrawal) => void;
  /** Id do saque com `approve` em voo, para desabilitar só aquele botão. */
  approvingId: string | undefined;
  /** Linha aberta no drawer, marcada na tabela. */
  selectedId?: string;
  /** Recorte por seller (`?seller_id=`): filtro com busca e chip com saída na barra da lista. */
  sellerId?: string;
  onSellerChange: (sellerId: string | undefined) => void;
  /** Injetado nos testes para a idade não depender do relógio da máquina. */
  now?: Date;
}

export function AdminWithdrawalsQueue({
  rows,
  tab,
  onTabChange,
  totals,
  isLoading,
  error,
  onRetry,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onSelect,
  onApprove,
  approvingId,
  selectedId,
  sellerId,
  onSellerChange,
  now,
}: AdminWithdrawalsQueueProps) {
  const current = tabTotals(totals, tab);
  // O agregado sai da MESMA transação da página (API-27), então ele nunca fica atrás das
  // linhas; o piso existe só para a primeira renderização, antes de `totals` chegar.
  const counted = Math.max(current?.count ?? rows.length, rows.length);
  const tabs: SegItem[] = TAB_IDS.map((id) => {
    const count = tabTotals(totals, id)?.count;
    return { id, label: TAB_LABEL[id], ...(count === undefined ? {} : { count }) };
  });

  const empty =
    tab === QUEUE_TAB && sellerId === undefined
      ? {
          emptyTitle: 'Fila vazia',
          emptyHint:
            'Nenhum saque aguardando aprovação. Assim que um seller pedir, ele aparece aqui.',
        }
      : {
          emptyTitle: 'Nenhum saque neste status',
          emptyHint: 'Troque a aba para ver os outros saques.',
        };

  return (
    <>
      <ListToolbar
        tabs={tabs}
        value={tab}
        onChange={(id: string) => {
          onTabChange(TAB_IDS.find((candidate) => candidate === id) ?? QUEUE_TAB);
        }}
        extra={<SellerFilter value={sellerId} onChange={onSellerChange} />}
        {...(current === undefined
          ? {}
          : {
              right: (
                <>
                  {counted} {tab === QUEUE_TAB ? 'nesta fila' : counted === 1 ? 'saque' : 'saques'}{' '}
                  · total <Amount cents={current.cents} size={12} weight={500} ink="muted" />
                </>
              ),
            })}
      />

      <Table
        cols={columnsFor(onSelect, onApprove, onSellerChange, approvingId, now)}
        rows={rows}
        rowKey={(row) => row.id}
        onRow={onSelect}
        {...(selectedId === undefined
          ? {}
          : { selected: (row: AdminWithdrawal) => row.id === selectedId })}
        isLoading={isLoading}
        error={error}
        onRetry={onRetry}
        {...empty}
      />

      {rows.length > 0 && (
        <Pager
          from={1}
          to={rows.length}
          total={counted}
          {...(hasMore && !isLoadingMore ? { onNext: onLoadMore } : {})}
        />
      )}
    </>
  );
}
